import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Link } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { useSelector } from 'react-redux';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import v2_actionInsightsAtendente from 'src/actions/v2/dashboard/relatorios/v2_actionInsightsAtendente';
import tarefasService from 'src/services/tarefasService';
import actionListaTicketsPorUsuario from 'src/actions/chamados/actionListaTicketsPorUsuario';
import { mascararTel } from 'src/utils/functions';
import { INICIO_DESIGN } from './Inicio.design';

const D = INICIO_DESIGN;

const ATALHOS = [
  {
    titulo: 'Chats',
    descricao: 'Converse com seus clientes em tempo real.',
    icon: 'eva:message-circle-outline',
    path: '/chats/chat',
  },
  {
    titulo: 'Automações',
    descricao: 'Crie fluxos automatizados para seu atendimento.',
    icon: 'eva:flash-outline',
    path: '/dashboard/automacoes',
  },
  {
    titulo: 'Configurações',
    descricao: 'Ajuste canais, equipe e preferências.',
    icon: 'eva:settings-2-outline',
    path: '/dashboard/configuracoes/geral',
  },
];

const TODAS_METRICAS = [
  { key: 'tmePessoal', label: 'TME', suffix: 's', icon: 'mdi:clock-outline', color: 'blue' },
  { key: 'tmaPessoal', label: 'TMA', suffix: 's', icon: 'mdi:clock-check', color: 'violet' },
  { key: 'foraSla', label: 'Fora SLA', icon: 'mdi:alert-circle', color: 'red' },
  { key: 'emAndamento', label: 'Em Andamento', icon: 'mdi:chat-processing', color: 'green' },
  { key: 'pendentes', label: 'Pendentes', icon: 'mdi:clock-alert', color: 'amber' },
  { key: 'avaliacaoMedia', label: 'Avaliação', icon: 'mdi:star', color: 'amber', format: (v) => `${v || 0}` },
  { key: 'sentimentoMedio', label: 'Sentimento', icon: 'mdi:emoticon', color: 'indigo' },
];

const TAREFAS_STATS_CONFIG = [
  { key: 'aFazer', label: 'A Fazer', color: 'amber' },
  { key: 'emProgresso', label: 'Em Progresso', color: 'blue' },
  { key: 'concluidas', label: 'Concluídas', color: 'green' },
  { key: 'atrasadas', label: 'Atrasadas', color: 'red' },
];

const CHAMADOS_STATS_CONFIG = [
  { key: 'pendentes', label: 'Pendentes', color: 'amber' },
  { key: 'abertos', label: 'Abertos', color: 'blue' },
  { key: 'concluidos', label: 'Concluídos', color: 'green' },
  { key: 'recusados', label: 'Recusados', color: 'red' },
];

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

export default function Inicio() {
  const { user } = useAuth();
  const firstName = user?.nomeFantasia?.split(' ')[0] || 'Usuário';
  const { globalConfig } = useSelector((state) => state.global);
  const links = globalConfig?.provider?.config?.links || {};
  const phoneSupport = globalConfig?.provider?.config?.phone_support;

  // Minhas Tarefas
  const [tarefas, setTarefas] = useState([]);
  const [tarefasLoading, setTarefasLoading] = useState(false);

  useEffect(() => {
    if (!user?.clienteId) return;
    async function fetchTarefas() {
      try {
        setTarefasLoading(true);
        const res = await tarefasService.listar(user.clienteId, { assigned_to: user.id });
        setTarefas(res.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setTarefasLoading(false);
      }
    }
    fetchTarefas();
  }, [user?.clienteId, user?.id]);

  const tarefasStats = useMemo(() => {
    const aFazer = tarefas.filter((t) => t.stage_name === 'A Fazer' || !t.stage_name).length;
    const emProgresso = tarefas.filter((t) => t.stage_name === 'Em Progresso').length;
    const concluidas = tarefas.filter((t) => t.stage_name === 'Concluído').length;
    const atrasadas = tarefas.filter((t) => t.due_date && !['Concluído'].includes(t.stage_name) && moment(t.due_date).isBefore(moment(), 'day')).length;
    return { aFazer, emProgresso, concluidas, atrasadas, total: tarefas.length };
  }, [tarefas]);

  const tarefasVisiveis = useMemo(() => {
    return tarefas.filter((t) => t.stage_name !== 'Concluído').slice(0, 5);
  }, [tarefas]);

  // Meus Chamados
  const [chamados, setChamados] = useState([]);
  const [chamadosLoading, setChamadosLoading] = useState(false);

  useEffect(() => {
    if (!user?.clienteId) return;
    async function fetchChamados() {
      try {
        setChamadosLoading(true);
        const querys = `?periodo=mes_atual&cliente_id=${user.clienteId}&usuario_id=${user.id}&inicio=&final=&action=null&search=`;
        const res = await actionListaTicketsPorUsuario(querys);
        setChamados(res.data.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setChamadosLoading(false);
      }
    }
    fetchChamados();
  }, [user?.clienteId, user?.id]);

  const chamadosStats = useMemo(() => {
    const pendentes = chamados.filter((c) => c.status === 'pendente').length;
    const abertos = chamados.filter((c) => c.status === 'aberto').length;
    const concluidos = chamados.filter((c) => c.status === 'concluido').length;
    const recusados = chamados.filter((c) => c.status === 'recusado').length;
    return { pendentes, abertos, concluidos, recusados, total: chamados.length };
  }, [chamados]);

  const chamadosVisiveis = useMemo(() => {
    return chamados.filter((c) => c.status !== 'concluido' && c.status !== 'cancelado').slice(0, 5);
  }, [chamados]);

  // Meu Painel
  const [periodo] = useState('today');
  const [inicial] = useState(new Date());
  const [final2State] = useState(new Date());
  const inicio2 = moment(inicial).format('YYYY-MM-DD');
  const final2 = moment(final2State).format('YYYY-MM-DD');
  const [painelLoading, setPainelLoading] = useState(false);
  const [painelData, setPainelData] = useState(null);

  useEffect(() => {
    if (!user?.clienteId) return;
    async function fetchPainel() {
      try {
        setPainelLoading(true);
        const querys = `?cliente_id=${user.clienteId}&periodo=${periodo}&inicio=${inicio2}&final=${final2}&usuario_id=${user.id}`;
        const res = await v2_actionInsightsAtendente(querys);
        setPainelData(res.data.data);
      } catch (error) {
        console.log(error);
      } finally {
        setPainelLoading(false);
      }
    }
    fetchPainel();
  }, [user?.clienteId, periodo, inicio2, final2]);

  // Recursos de ajuda dinâmicos
  const helpItems = useMemo(() => {
    const items = [];
    if (links.showAppWindows) {
      items.push({ icon: 'mdi:microsoft-windows', label: 'App Windows', description: 'Baixe o aplicativo para desktop.', color: 'blue', onClick: () => window.open(links.app_windows_url, '_blank') });
    }
    if (links.showAppAndroid) {
      items.push({ icon: 'mdi:android', label: 'App Android', description: 'Baixe o app na Play Store.', color: 'green', onClick: () => window.open(links.playstore_url, '_blank') });
    }
    if (links.course_url) {
      items.push({ icon: 'mdi:school-outline', label: 'Curso', description: 'Aprenda as funções da plataforma.', color: 'violet', onClick: () => window.open(links.course_url, '_blank') });
    }
    if (links.showDocsApi) {
      items.push({ icon: 'mdi:file-document-outline', label: 'Documentação API', description: 'Integre com nossa API.', color: 'indigo', onClick: () => window.open(links.api_docs_url, '_blank') });
    }
    if (links.feedback) {
      items.push({ icon: 'mdi:message-alert-outline', label: 'Relatar Problema', description: 'Abra um chamado de suporte.', color: 'amber', onClick: () => window.open(links.feedback_url, '_blank') });
    }
    if (links.news) {
      items.push({ icon: 'mdi:newspaper-variant-outline', label: 'Novidades', description: 'Veja as últimas atualizações.', color: 'indigo', onClick: () => window.open(links.news_url, '_blank') });
    }
    return items;
  }, [links]);

  return (
    <div className={D.page.container}>
      <ScrollArea className={D.page.content}>
        <div className={D.page.innerPadding}>

          {/* ── Greeting simples ── */}
          <div className={D.greeting.wrapper}>
            <h1 className={D.greeting.title}>
              {getGreeting()}, {firstName}!
            </h1>
            <p className={D.greeting.subtitle}>
              {moment().locale('pt-br').format('dddd, DD [de] MMMM [de] YYYY')}
            </p>
          </div>

          {/* ── Shortcuts grandes ── */}
          <div className={D.shortcuts.grid}>
            {ATALHOS.map((atalho) => (
              <Link key={atalho.titulo} to={atalho.path} className={D.shortcuts.card}>
                <div className={D.shortcuts.headerRow}>
                  <span className={D.shortcuts.title}>{atalho.titulo}</span>
                  <div className={D.shortcuts.iconBox}>
                    <Iconify icon={atalho.icon} width={20} className={D.shortcuts.icon} />
                  </div>
                </div>
                <p className={D.shortcuts.description}>{atalho.descricao}</p>
              </Link>
            ))}
          </div>

          {/* ── Métricas ── */}
          <div className={D.metricas.grid}>
            {TODAS_METRICAS.map((m) => {
              const rawVal = painelData?.[m.key] || 0;
              const valor = m.format ? m.format(rawVal) : rawVal;
              const display = painelLoading ? '—' : typeof valor === 'number' ? valor.toLocaleString('pt-BR') : valor;
              return (
                <div key={m.key} className={D.metricas.card}>
                  <div className={`${D.metricas.cardIconBox} ${D.metricas.colors[m.color]?.bg}`}>
                    <Iconify icon={m.icon} width={18} className={D.metricas.colors[m.color]?.text} />
                  </div>
                  <div className={D.metricas.cardInfo}>
                    <p className={D.metricas.cardValue}>{display}{m.suffix && !painelLoading ? m.suffix : ''}</p>
                    <p className={D.metricas.cardLabel}>{m.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Tarefas + Chamados ── */}
          <div className={D.twoColumns.grid}>
            {/* Tarefas */}
            <div>
              <div className={D.tarefas.card}>
                <div className={D.tarefas.header}>
                  <div className={D.tarefas.headerLeft}>
                    <div className={D.tarefas.headerIcon}>
                      <Iconify icon="eva:checkmark-square-outline" width={16} className="text-[#7C3AED]" />
                    </div>
                    <span className={D.tarefas.headerTitle}>Minhas Tarefas</span>
                  </div>
                  <Link to="/dashboard/tarefas" className={D.tarefas.headerLink}>
                    Ver todas
                    <Iconify icon="eva:arrow-forward-outline" width={14} />
                  </Link>
                </div>

                <div className={D.tarefas.statsRow}>
                  {TAREFAS_STATS_CONFIG.map((stat) => (
                    <div key={stat.key} className={`${D.tarefas.statPill} ${D.tarefas.statBg[stat.color]}`}>
                      <span className={`${D.tarefas.statValue} ${D.tarefas.statText[stat.color]}`}>
                        {tarefasLoading ? '—' : tarefasStats[stat.key]}
                      </span>
                      <span className={D.tarefas.statLabel}>{stat.label}</span>
                    </div>
                  ))}
                </div>

                <div className={D.tarefas.statDivider} />

                {tarefasLoading ? (
                  <div className={D.tarefas.list}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={D.tarefas.skeleton}>
                        <div className={`${D.tarefas.skeletonBar} w-4 h-4 !rounded-full`} />
                        <div className="flex-1 space-y-2">
                          <div className={`${D.tarefas.skeletonBar} w-3/4`} />
                          <div className={`${D.tarefas.skeletonBar} w-1/2 h-2`} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : tarefasVisiveis.length === 0 ? (
                  <div className={D.tarefas.empty}>
                    <div className={D.tarefas.emptyIcon}>
                      <Iconify icon="eva:clipboard-outline" width={22} className="text-gray-400" />
                    </div>
                    <p className={D.tarefas.emptyText}>Nenhuma tarefa pendente</p>
                    <p className={D.tarefas.emptySubtext}>Suas tarefas atribuídas aparecerão aqui</p>
                  </div>
                ) : (
                  <div className={D.tarefas.list}>
                    {tarefasVisiveis.map((tarefa) => {
                      const statusClass = D.tarefas.statusColors[tarefa.stage_name] || 'bg-gray-50 text-gray-700 dark:bg-gray-500/10 dark:text-gray-400';
                      const priorityClass = D.tarefas.priorityColors[tarefa.priority] || D.tarefas.priorityColors.low;
                      const isOverdue = tarefa.due_date && moment(tarefa.due_date).isBefore(moment(), 'day');
                      return (
                        <Link key={tarefa._id} to="/dashboard/tarefas" className={D.tarefas.item}>
                          <div className={`${D.tarefas.priorityDot} ${priorityClass}`} />
                          <div className={D.tarefas.itemContent}>
                            <p className={D.tarefas.itemTitle}>{tarefa.title}</p>
                            <div className={D.tarefas.itemMeta}>
                              <span className={`${D.tarefas.badge} ${statusClass}`}>
                                {tarefa.stage_name || 'A Fazer'}
                              </span>
                              {tarefa.due_date && (
                                <span className={isOverdue ? D.tarefas.itemDateOverdue : D.tarefas.itemDate}>
                                  {isOverdue && <Iconify icon="mdi:alert-circle" width={11} />}
                                  {moment(tarefa.due_date).format('DD/MM')}
                                </span>
                              )}
                            </div>
                          </div>
                          <Iconify icon="eva:chevron-right-outline" width={16} className={D.tarefas.chevron} />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Chamados */}
            <div>
              <div className={D.chamados.card}>
                <div className={D.chamados.header}>
                  <div className={D.chamados.headerLeft}>
                    <div className={D.chamados.headerIcon}>
                      <Iconify icon="mdi:ticket-confirmation" width={16} className="text-blue-600" />
                    </div>
                    <span className={D.chamados.headerTitle}>Meus Chamados</span>
                  </div>
                  <Link to="/dashboard/chamados" className={D.chamados.headerLink}>
                    Ver todos
                    <Iconify icon="eva:arrow-forward-outline" width={14} />
                  </Link>
                </div>

                <div className={D.chamados.statsRow}>
                  {CHAMADOS_STATS_CONFIG.map((stat) => (
                    <div key={stat.key} className={`${D.chamados.statPill} ${D.chamados.statBg[stat.color]}`}>
                      <span className={`${D.chamados.statValue} ${D.chamados.statText[stat.color]}`}>
                        {chamadosLoading ? '—' : chamadosStats[stat.key]}
                      </span>
                      <span className={D.chamados.statLabel}>{stat.label}</span>
                    </div>
                  ))}
                </div>

                <div className={D.chamados.statDivider} />

                {chamadosLoading ? (
                  <div className={D.chamados.list}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={D.chamados.skeleton}>
                        <div className={`${D.chamados.skeletonBar} w-4 h-4 !rounded-full`} />
                        <div className="flex-1 space-y-2">
                          <div className={`${D.chamados.skeletonBar} w-3/4`} />
                          <div className={`${D.chamados.skeletonBar} w-1/2 h-2`} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : chamadosVisiveis.length === 0 ? (
                  <div className={D.chamados.empty}>
                    <div className={D.chamados.emptyIcon}>
                      <Iconify icon="mdi:ticket-outline" width={22} className="text-gray-400" />
                    </div>
                    <p className={D.chamados.emptyText}>Nenhum chamado pendente</p>
                    <p className={D.chamados.emptySubtext}>Seus chamados aparecerão aqui</p>
                  </div>
                ) : (
                  <div className={D.chamados.list}>
                    {chamadosVisiveis.map((chamado) => {
                      const statusClass = D.chamados.statusColors[chamado.status] || 'bg-gray-50 text-gray-700 dark:bg-gray-500/10 dark:text-gray-400';
                      const statusLabel = D.chamados.statusLabels[chamado.status] || chamado.status;
                      return (
                        <Link key={chamado._id} to="/dashboard/chamados" className={D.chamados.item}>
                          <div className={D.chamados.itemContent}>
                            <p className={D.chamados.itemTitle}>{chamado.titulo || chamado.assunto}</p>
                            <div className={D.chamados.itemMeta}>
                              <span className={`${D.chamados.badge} ${statusClass}`}>
                                {statusLabel}
                              </span>
                              {chamado.chamado && (
                                <span className={D.chamados.chamadoNum}>#{chamado.chamado}</span>
                              )}
                            </div>
                          </div>
                          <Iconify icon="eva:chevron-right-outline" width={16} className={D.chamados.chevron} />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Toda ajuda que você precisa ── */}
          <div>
            <h2 className={D.help.title}>Toda ajuda que você precisa</h2>

            {/* Suporte WhatsApp */}
            {phoneSupport && (
              <div className={D.help.supportCard}>
                <div className={D.help.supportIconBox}>
                  <Iconify icon="ic:baseline-whatsapp" width={24} className="text-white" />
                </div>
                <div className={D.help.supportContent}>
                  <p className={D.help.supportTitle}>Suporte via WhatsApp</p>
                  <p className={D.help.supportPhone}>{mascararTel(phoneSupport)}</p>
                </div>
                <button
                  onClick={() => window.open(`https://api.whatsapp.com/send/?phone=${phoneSupport}&text=Ol%C3%A1,+sou+cliente+e+preciso+de+suporte&type=phone_number&app_absent=0`, '_blank')}
                  className={D.help.supportButton}
                >
                  <Iconify icon="eva:message-circle-outline" width={16} />
                  Conversar com suporte
                </button>
              </div>
            )}

            {/* Grid de recursos */}
            {helpItems.length > 0 && (
              <div className={D.help.resourcesGrid}>
                {helpItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className={D.help.resourceCard}
                  >
                    <div className={`${D.help.resourceIconBox} ${D.metricas.colors[item.color]?.bg}`}>
                      <Iconify icon={item.icon} width={22} className={D.metricas.colors[item.color]?.text} />
                    </div>
                    <span className={D.help.resourceLabel}>{item.label}</span>
                    <p className={D.help.resourceDescription}>{item.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </ScrollArea>
    </div>
  );
}
